/**
 * CVC Customer Customers Tab Component
 * Displays customer engagement data from CVC program with rich dashboard design
 */

import { Callout, Checkbox, DirectionalHint, Icon, IconButton, Link, SearchBox, Spinner, SpinnerSize, TooltipHost } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { LinkRegular } from "@fluentui/react-icons";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { CustomDataGrid } from "../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { IRows } from "../../SharedComponents/Interfaces/CustomDataGrid.interfaces";
import { useGetCVCCustomersQuery, useLazyGetCVCCustomerNotesQuery } from "../../slices/CVCCustomer.slice";
import {
    ICVCCustomerCustomersTabProps,
    ICVCCustomer,
    ICVCAggregatedCustomer,
    ICVCCustomerChartData,
    ILastEngagedBucket,
    ICVCCustomerInlineNote
} from "./CVCCustomer.interfaces";
import { CVC_CUSTOMER_CUSTOMERS_STYLES, CVC_CUSTOMER_STYLES } from "./CVCCustomer.styles";
import { SOLUTION_COLORS, LAST_ENGAGED_BUCKETS, CVC_CUSTOMER_FILTER_STORAGE_PREFIX } from "./CVCCustomer.constants";
import FluentDonutChart from "../../ChartComponents/FluentDountChart";
import CustomCard from "../../SharedComponents/CustomCard";
import { getNextColor } from "@fluentui/react-charting";
import CVCCustomerAICompanionSection from "./CVCCustomerAICompanionSection";
import CVCCustomerNotesPanel from "./CVCCustomerNotesPanel";

/**
 * Searchable Multi-Select Dropdown Component with Virtual Scrolling
 */
interface SearchableMultiSelectProps {
    placeholder: string;
    options: string[];
    selectedKeys: string[];
    onChange: (selectedKeys: string[]) => void;
    width?: string;
    preserveOrder?: boolean;
}

const ITEMS_PER_PAGE = 50;

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
    placeholder,
    options,
    selectedKeys,
    onChange,
    width = "100%",
    preserveOrder = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
    const buttonRef = useRef<HTMLDivElement>(null);
    const calloutRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Reset visible count when dropdown opens or search changes
    useEffect(() => {
        setVisibleCount(ITEMS_PER_PAGE);
    }, [isOpen, searchText]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isClickInsideButton = buttonRef.current?.contains(target);
            const isClickInsideCallout = calloutRef.current?.contains(target);

            if (!isClickInsideButton && !isClickInsideCallout) {
                setIsOpen(false);
                setSearchText("");
            }
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        let filtered = options;
        if (searchText) {
            const lowerSearch = searchText.toLowerCase();
            filtered = options.filter((opt) => opt.toLowerCase().includes(lowerSearch));
        }
        // Sort: selected items first, then alphabetically (unless preserveOrder is set)
        if (preserveOrder) {
            // Keep original order from options array, just move selected items to top
            return filtered.sort((a, b) => {
                const aSelected = selectedKeys.includes(a);
                const bSelected = selectedKeys.includes(b);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                // Preserve original order by comparing indices
                return options.indexOf(a) - options.indexOf(b);
            });
        }
        return filtered.sort((a, b) => {
            const aSelected = selectedKeys.includes(a);
            const bSelected = selectedKeys.includes(b);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return a.localeCompare(b);
        });
    }, [options, searchText, selectedKeys, preserveOrder]);

    // Only render visible items for performance
    const visibleOptions = useMemo(() => {
        return filteredOptions.slice(0, visibleCount);
    }, [filteredOptions, visibleCount]);

    const hasMore = filteredOptions.length > visibleCount;

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (scrollBottom < 50 && hasMore) {
            setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
        }
    }, [hasMore]);

    const handleToggle = (option: string) => {
        if (selectedKeys.includes(option)) {
            onChange(selectedKeys.filter((k) => k !== option));
        } else {
            onChange([...selectedKeys, option]);
        }
    };

    const displayText = selectedKeys.length > 0
        ? selectedKeys.length === 1
            ? selectedKeys[0]
            : `${selectedKeys.length} selected`
        : placeholder;

    return (
        <div style={{ width, position: "relative" }}>
            <div
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0 12px",
                    height: "36px",
                    background: "#FFFFFF",
                    border: "1px solid #E1DFDD",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: selectedKeys.length > 0 ? "#333" : "#666",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                }}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {displayText}
                </span>
                <Icon iconName="ChevronDown" style={{ fontSize: 12, color: "#605E5C", marginLeft: 8, flexShrink: 0 }} />
            </div>

            {isOpen && (
                <Callout
                    target={buttonRef.current}
                    onDismiss={() => {
                        setIsOpen(false);
                        setSearchText("");
                    }}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    isBeakVisible={false}
                    gapSpace={2}
                    setInitialFocus={false}
                    styles={{
                        root: { boxShadow: "0 4px 16px rgba(0,0,0,0.12)", borderRadius: "8px" },
                        calloutMain: { borderRadius: "8px", minWidth: 200, maxWidth: 300 }
                    }}
                >
                    <div ref={calloutRef} style={{ padding: "8px" }}>
                        <SearchBox
                            placeholder="Search..."
                            value={searchText}
                            onChange={(_, newValue) => setSearchText(newValue || "")}
                            styles={{
                                root: { marginBottom: 8 },
                                field: { fontSize: 13 }
                            }}
                            autoFocus
                        />
                        <div
                            ref={listRef}
                            style={{ maxHeight: 250, overflowY: "auto" }}
                            onScroll={handleScroll}
                        >
                            {filteredOptions.length === 0 ? (
                                <div style={{ padding: "8px 12px", color: "#666", fontSize: 13 }}>No matches found</div>
                            ) : (
                                visibleOptions.map((option) => (
                                    <div
                                        key={option}
                                        onClick={() => handleToggle(option)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            background: selectedKeys.includes(option) ? "#e6f2fb" : "transparent",
                                            borderRadius: 4
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!selectedKeys.includes(option)) {
                                                e.currentTarget.style.background = "#f3f2f1";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = selectedKeys.includes(option) ? "#e6f2fb" : "transparent";
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedKeys.includes(option)}
                                            styles={{ root: { marginRight: 8, pointerEvents: "none" } }}
                                        />
                                        <span style={{ fontSize: 13, color: "#333" }}>{option || "(Blank)"}</span>
                                    </div>
                                ))
                            )}
                            {hasMore && (
                                <div style={{ padding: "8px 12px", color: "#0078D4", fontSize: 12, textAlign: "center" }}>
                                    Scroll for more...
                                </div>
                            )}
                        </div>
                    </div>
                </Callout>
            )}
        </div>
    );
};

/**
 * Stat Card Component
 */
const StatCard: React.FC<{
    icon: string;
    iconColor: string;
    iconBgColor: string;
    value: number | string;
    label: string;
    change?: number;
}> = ({ icon, iconColor, iconBgColor, value, label, change }) => (
    <div
        style={{
            background: "#FFFFFF",
            borderRadius: "12px",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            border: "1px solid #E6E9ED",
            flex: 1,
            minWidth: "200px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            position: "relative"
        }}
    >
        <TooltipHost
            content={label}
            styles={{
                root: {
                    position: "absolute",
                    top: 5,
                    right: 5,
                    display: "inline-flex"
                }
            }}
        >
            <IconButton
                ariaLabel={`Information about ${label}`}
                iconProps={{ iconName: "Info" }}
                styles={{
                    root: {
                        borderRadius: 6
                    }
                }}
            />
        </TooltipHost>
        <div
            style={{
                width: "48px",
                height: "48px",
                borderRadius: "10px",
                background: iconBgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                color: iconColor,
                flexShrink: 0
            }}
        >
            <Icon iconName={icon} />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span
                    style={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: "#1A2A45",
                        lineHeight: 1.2
                    }}
                >
                    {value}
                </span>
                {change !== undefined && change > 0 && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "4px 10px",
                            borderRadius: "20px",
                            background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                            boxShadow: "0 2px 6px rgba(39, 174, 96, 0.2)"
                        }}
                    >
                        <Icon
                            iconName="ChevronUpSmall"
                            style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#27ae60"
                            }}
                        />
                        <span
                            style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#27ae60"
                            }}
                        >
                            +{change}
                        </span>
                    </div>
                )}
            </div>
            <span
                style={{
                    fontSize: "13px",
                    color: "#7f8c8d",
                    marginTop: "2px"
                }}
            >
                {label}
            </span>
        </div>
    </div>
);

/**
 * Calculate days since last engaged
 */
const calculateDaysSinceLastEngaged = (dateStr: string | null): number => {
    if (!dateStr) return -1;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return -1;
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get last engaged bucket for a date
 */
const getLastEngagedBucket = (dateStr: string | null): string => {
    const days = calculateDaysSinceLastEngaged(dateStr);
    if (days === -1) return "Unknown";

    for (const bucket of LAST_ENGAGED_BUCKETS) {
        if (bucket.key === "Unknown") continue;
        if (days >= bucket.minDays && days <= bucket.maxDays) {
            return bucket.key;
        }
    }
    return "Unknown";
};

/**
 * Donut Chart Filters Component
 */
const DonutChartFilters: React.FC<{
    l4ManagerData: ICVCCustomerChartData[];
    selectedL4Managers: string[];
    onL4ManagerClick: (managers: string[]) => void;
    solutionData: ICVCCustomerChartData[];
    selectedSolutions: string[];
    onSolutionClick: (solutions: string[]) => void;
    lastEngagedData: ILastEngagedBucket[];
    selectedLastEngaged: string[];
    onLastEngagedClick: (keys: string[]) => void;
    totalEngagements: number;
}> = ({
    l4ManagerData,
    selectedL4Managers,
    onL4ManagerClick,
    solutionData,
    selectedSolutions,
    onSolutionClick,
    lastEngagedData,
    selectedLastEngaged,
    onLastEngagedClick,
    totalEngagements
}) => {
        // L4 Manager (CVC Leadership) chart data
        const l4ManagerChartData = useMemo(() => {
            return l4ManagerData.map((item, index) => ({
                legend: item.stage,
                data: item.count,
                color: getNextColor(index),
                onClick: () => {
                    if (selectedL4Managers.includes(item.stage)) {
                        onL4ManagerClick(selectedL4Managers.filter((s) => s !== item.stage));
                    } else {
                        onL4ManagerClick([...selectedL4Managers, item.stage]);
                    }
                }
            }));
        }, [l4ManagerData, selectedL4Managers, onL4ManagerClick]);

        // Solution chart data
        const solutionChartData = useMemo(() => {
            return solutionData.map((item, index) => ({
                legend: item.stage,
                data: item.count,
                color: SOLUTION_COLORS[item.stage] || getNextColor(index + 5),
                onClick: () => {
                    if (selectedSolutions.includes(item.stage)) {
                        onSolutionClick(selectedSolutions.filter((s) => s !== item.stage));
                    } else {
                        onSolutionClick([...selectedSolutions, item.stage]);
                    }
                }
            }));
        }, [solutionData, selectedSolutions, onSolutionClick]);

        // Last Engaged chart data
        const lastEngagedChartData = useMemo(() => {
            return lastEngagedData.map((bucket) => ({
                legend: bucket.label,
                data: bucket.count,
                color: bucket.color,
                onClick: () => {
                    if (selectedLastEngaged.includes(bucket.key)) {
                        onLastEngagedClick(selectedLastEngaged.filter((k) => k !== bucket.key));
                    } else {
                        onLastEngagedClick([...selectedLastEngaged, bucket.key]);
                    }
                }
            }));
        }, [lastEngagedData, selectedLastEngaged, onLastEngagedClick]);

        return (
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                {/* Engagements by CVC Leadership (L4) */}
                <CustomCard
                    cardHeaderTitle="Engagements by CVC Leadership (L4)"
                    styles={{ root: { flex: 1, minWidth: 350, minHeight: 320 } }}
                >
                    {selectedL4Managers.length > 0 && (
                        <div style={{ position: "absolute", top: 12, right: 12 }}>
                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#0078D4",
                                    cursor: "pointer",
                                    textDecoration: "underline"
                                }}
                                onClick={() => onL4ManagerClick([])}
                            >
                                Clear Filter ({selectedL4Managers.length})
                            </span>
                        </div>
                    )}
                    <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FluentDonutChart
                            data={{
                                chartTitle: "Engagements by CVC Leadership (L4)",
                                chartData: l4ManagerChartData
                            }}
                            height={230}
                            width={300}
                            innerRadius={50}
                            valueInsideDonut={totalEngagements}
                        />
                    </div>
                </CustomCard>

                {/* Solution by Engagements */}
                <CustomCard
                    cardHeaderTitle="Solution by Engagements"
                    styles={{ root: { flex: 1, minWidth: 350, minHeight: 320 } }}
                >
                    {selectedSolutions.length > 0 && (
                        <div style={{ position: "absolute", top: 12, right: 12 }}>
                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#0078D4",
                                    cursor: "pointer",
                                    textDecoration: "underline"
                                }}
                                onClick={() => onSolutionClick([])}
                            >
                                Clear Filter ({selectedSolutions.length})
                            </span>
                        </div>
                    )}
                    <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FluentDonutChart
                            data={{
                                chartTitle: "Solution by Engagements",
                                chartData: solutionChartData
                            }}
                            height={230}
                            width={300}
                            innerRadius={50}
                            valueInsideDonut={totalEngagements}
                        />
                    </div>
                </CustomCard>

                {/* Last Engaged (days since latest notes) */}
                <CustomCard
                    cardHeaderTitle="Last Engaged (days since latest notes)"
                    styles={{ root: { flex: 1, minWidth: 350, minHeight: 320 } }}
                >
                    {selectedLastEngaged.length > 0 && (
                        <div style={{ position: "absolute", top: 12, right: 12 }}>
                            <span
                                style={{
                                    fontSize: "12px",
                                    color: "#059669",
                                    cursor: "pointer",
                                    textDecoration: "underline"
                                }}
                                onClick={() => onLastEngagedClick([])}
                            >
                                Clear Filter ({selectedLastEngaged.length})
                            </span>
                        </div>
                    )}
                    <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FluentDonutChart
                            data={{
                                chartTitle: "Last Engaged",
                                chartData: lastEngagedChartData
                            }}
                            height={230}
                            width={300}
                            innerRadius={50}
                            valueInsideDonut={totalEngagements}
                        />
                    </div>
                </CustomCard>
            </div>
        );
    };

/**
 * Main CVC Customer Customers Tab Component
 */
export const CVCCustomerCustomersTab: React.FC<ICVCCustomerCustomersTabProps> = ({ isLoading: externalLoading }) => {
    // API query - data comes from static file or API (no localStorage caching)
    const { data: customersResponse, isLoading: isLoadingCustomers, error, isFetching } = useGetCVCCustomersQuery();

    // Lazy trigger to load full notes on demand (kept out of the main list payload)
    const [fetchCustomerNotes] = useLazyGetCVCCustomerNotesQuery();

    // Use fresh data from static file or API
    const effectiveData = customersResponse;
    const isActuallyLoading = isLoadingCustomers;

    // Filter state with localStorage persistence
    const [selectedCustomers, setSelectedCustomers] = useLocalStorage<string[]>(`${CVC_CUSTOMER_FILTER_STORAGE_PREFIX}-customers`, []);
    const [selectedSolutions, setSelectedSolutions] = useLocalStorage<string[]>(`${CVC_CUSTOMER_FILTER_STORAGE_PREFIX}-solutions`, []);
    const [selectedL4Managers, setSelectedL4Managers] = useLocalStorage<string[]>(`${CVC_CUSTOMER_FILTER_STORAGE_PREFIX}-l4managers`, []);
    const [selectedL5Managers, setSelectedL5Managers] = useLocalStorage<string[]>(`${CVC_CUSTOMER_FILTER_STORAGE_PREFIX}-l5managers`, []);
    const [selectedL6Managers, setSelectedL6Managers] = useLocalStorage<string[]>(`${CVC_CUSTOMER_FILTER_STORAGE_PREFIX}-l6managers`, []);
    const [selectedOwners, setSelectedOwners] = useLocalStorage<string[]>(`${CVC_CUSTOMER_FILTER_STORAGE_PREFIX}-owners`, []);
    const [selectedLastEngaged, setSelectedLastEngaged] = useLocalStorage<string[]>(`${CVC_CUSTOMER_FILTER_STORAGE_PREFIX}-lastengaged`, []);
    const [searchQuery, setSearchQuery] = useState("");

    // Notes panel state
    const [isNotesPanelOpen, { setTrue: openNotesPanel, setFalse: closeNotesPanel }] = useBoolean(false);
    const [isLoadingNotes, setIsLoadingNotes] = useState(false);
    const [selectedCustomerForNotes, setSelectedCustomerForNotes] = useState<{
        customerName: string;
        productName: string;
        customerProductURL: string;
        notesData: ICVCCustomerInlineNote[];
    } | null>(null);

    // Clear all filters on component mount
    useEffect(() => {
        setSelectedCustomers([]);
        setSelectedSolutions([]);
        setSelectedL4Managers([]);
        setSelectedL5Managers([]);
        setSelectedL6Managers([]);
        setSelectedOwners([]);
        setSelectedLastEngaged([]);
        setSearchQuery("");
    }, [setSelectedCustomers, setSelectedSolutions, setSelectedL4Managers, setSelectedL5Managers, setSelectedL6Managers, setSelectedOwners, setSelectedLastEngaged]);

    // Get unique filter options
    const filterOptions = useMemo(() => {
        if (!effectiveData?.customers) return {
            customers: [],
            solutions: [],
            l4Managers: [],
            l5Managers: [],
            l6Managers: [],
            owners: []
        };

        const allCustomers = effectiveData.customers;

        // Base filter options (always from full dataset)
        const customers = Array.from(new Set(allCustomers.map((c: ICVCCustomer) => c.crmCustomerName))).filter(Boolean).sort() as string[];
        const solutions = Array.from(new Set(allCustomers.map((c: ICVCCustomer) => c.crmProductName))).filter(Boolean).sort() as string[];
        const owners = Array.from(new Set(allCustomers.map((c: ICVCCustomer) => c.owner))).filter(Boolean).sort() as string[];

        // L4 Managers - always show all
        const l4Managers = Array.from(new Set(allCustomers.map((c: ICVCCustomer) => c.l4Manager))).filter(Boolean).sort() as string[];

        // L5 Managers - cascade from L4 selection
        let l5FilteredCustomers = allCustomers;
        if (selectedL4Managers.length > 0) {
            l5FilteredCustomers = allCustomers.filter((c: ICVCCustomer) => selectedL4Managers.includes(c.l4Manager));
        }
        const l5Managers = Array.from(new Set(l5FilteredCustomers.map((c: ICVCCustomer) => c.l5Manager))).filter(Boolean).sort() as string[];

        // L6 Managers - cascade from L5 selection (or L4 if no L5 selected)
        let l6FilteredCustomers = allCustomers;
        if (selectedL5Managers.length > 0) {
            l6FilteredCustomers = allCustomers.filter((c: ICVCCustomer) => selectedL5Managers.includes(c.l5Manager));
        } else if (selectedL4Managers.length > 0) {
            l6FilteredCustomers = allCustomers.filter((c: ICVCCustomer) => selectedL4Managers.includes(c.l4Manager));
        }
        const l6Managers = Array.from(new Set(l6FilteredCustomers.map((c: ICVCCustomer) => c.l6Manager))).filter(Boolean).sort() as string[];

        return { customers, solutions, l4Managers, l5Managers, l6Managers, owners };
    }, [effectiveData, selectedL4Managers, selectedL5Managers]);

    // Clear L5 and L6 selections when L4 changes and selected values are no longer valid
    useEffect(() => {
        if (filterOptions.l5Managers.length > 0 && selectedL5Managers.length > 0) {
            const validL5Selections = selectedL5Managers.filter(l5 => filterOptions.l5Managers.includes(l5));
            if (validL5Selections.length !== selectedL5Managers.length) {
                setSelectedL5Managers(validL5Selections);
            }
        }
    }, [filterOptions.l5Managers, selectedL5Managers, setSelectedL5Managers]);

    // Clear L6 selections when L5 changes and selected values are no longer valid
    useEffect(() => {
        if (filterOptions.l6Managers.length > 0 && selectedL6Managers.length > 0) {
            const validL6Selections = selectedL6Managers.filter(l6 => filterOptions.l6Managers.includes(l6));
            if (validL6Selections.length !== selectedL6Managers.length) {
                setSelectedL6Managers(validL6Selections);
            }
        }
    }, [filterOptions.l6Managers, selectedL6Managers, setSelectedL6Managers]);

    // Step 1: Filter customers by all filters EXCEPT Last Engaged
    // This is used to compute the donut chart counts and the latest note dates
    const baseFilteredCustomers = useMemo((): ICVCCustomer[] => {
        if (!effectiveData?.customers) return [];

        return effectiveData.customers.filter((customer: ICVCCustomer) => {
            // Search filter
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const matchesSearch =
                    customer.crmCustomerName?.toLowerCase().includes(query) ||
                    customer.crmProductName?.toLowerCase().includes(query) ||
                    customer.l4Manager?.toLowerCase().includes(query) ||
                    customer.l5Manager?.toLowerCase().includes(query) ||
                    customer.l6Manager?.toLowerCase().includes(query) ||
                    customer.owner?.toLowerCase().includes(query);
                if (!matchesSearch) return false;
            }

            // Customer filter
            if (selectedCustomers.length > 0 && !selectedCustomers.includes(customer.crmCustomerName)) {
                return false;
            }

            // Solution filter
            if (selectedSolutions.length > 0 && !selectedSolutions.includes(customer.crmProductName)) {
                return false;
            }

            // CVC Leadership (L4) filter
            if (selectedL4Managers.length > 0 && !selectedL4Managers.includes(customer.l4Manager)) {
                return false;
            }

            // CVC Portfolio Lead (L5) filter
            if (selectedL5Managers.length > 0 && !selectedL5Managers.includes(customer.l5Manager)) {
                return false;
            }

            // CVC Lead (L6) filter
            if (selectedL6Managers.length > 0 && !selectedL6Managers.includes(customer.l6Manager)) {
                return false;
            }

            // Owner filter
            if (selectedOwners.length > 0 && !selectedOwners.includes(customer.owner)) {
                return false;
            }

            // Note: Last Engaged filter is NOT applied here
            return true;
        });
    }, [effectiveData, searchQuery, selectedCustomers, selectedSolutions, selectedL4Managers, selectedL5Managers, selectedL6Managers, selectedOwners]);

    // Step 2: Compute latest note date per engagement from baseFilteredCustomers
    // This ensures the donut chart and filter use the same data
    const latestNoteDateByEngagement = useMemo(() => {
        const latestDates = new Map<string, string>();

        baseFilteredCustomers.forEach((customer: ICVCCustomer) => {
            const key = `${customer.crmCustomerName}_${customer.crmProductName}`;
            const existingDate = latestDates.get(key);
            const currentDate = customer.modifiedOn1;

            if (!existingDate) {
                if (currentDate) latestDates.set(key, currentDate);
            } else if (currentDate) {
                // Keep the more recent date
                if (new Date(currentDate) > new Date(existingDate)) {
                    latestDates.set(key, currentDate);
                }
            }
        });

        return latestDates;
    }, [baseFilteredCustomers]);

    // Step 3: Apply Last Engaged filter on top of baseFilteredCustomers
    const filteredCustomers = useMemo((): ICVCCustomer[] => {
        if (selectedLastEngaged.length === 0) {
            return baseFilteredCustomers;
        }

        return baseFilteredCustomers.filter((customer: ICVCCustomer) => {
            // Last Engaged filter - use latest note date for the customer+product engagement
            // This ensures filtering matches the donut chart counts (which are per-engagement, not per-note)
            const engagementKey = `${customer.crmCustomerName}_${customer.crmProductName}`;
            const latestNoteDate = latestNoteDateByEngagement.get(engagementKey) || null;
            const bucket = getLastEngagedBucket(latestNoteDate);
            return selectedLastEngaged.includes(bucket);
        });
    }, [baseFilteredCustomers, selectedLastEngaged, latestNoteDateByEngagement]);

    // Aggregate customers by customer+product to show distinct Customer Solution Engagements
    // Each row represents a unique customer + product combination with notes count
    const aggregatedCustomers = useMemo((): ICVCAggregatedCustomer[] => {
        if (!filteredCustomers.length) return [];

        const aggregationMap = new Map<string, {
            baseCustomer: ICVCCustomer;
            notesCount: number;
            latestNoteDate: Date | null;
            keywordsSet: Set<string>;
        }>();

        filteredCustomers.forEach(customer => {
            // Use customer name + product name as the key
            const key = `${customer.crmCustomerName}_${customer.crmProductName}`;

            const existing = aggregationMap.get(key);
            const customerNoteDate = customer.modifiedOn1 ? new Date(customer.modifiedOn1) : null;

            // Split this record's keywords (comma separated) into individual trimmed values
            const recordKeywords = (customer.keywords || "")
                .split(",")
                .map(kw => kw.trim())
                .filter(kw => kw.length > 0);

            if (!existing) {
                // First occurrence - use this as the base customer
                aggregationMap.set(key, {
                    baseCustomer: customer,
                    notesCount: customer.hasNotes ? 1 : 0,
                    latestNoteDate: customerNoteDate,
                    keywordsSet: new Set(recordKeywords)
                });
            } else {
                // Additional notes for the same customer+product
                if (customer.hasNotes) {
                    existing.notesCount += 1;
                }
                // Collect keywords across all records in this group
                recordKeywords.forEach(kw => existing.keywordsSet.add(kw));
                // Keep track of the latest note date
                if (customerNoteDate && (!existing.latestNoteDate || customerNoteDate > existing.latestNoteDate)) {
                    existing.latestNoteDate = customerNoteDate;
                    // Update latestNote text if this is more recent
                    existing.baseCustomer = {
                        ...existing.baseCustomer,
                        latestNote: customer.latestNote || existing.baseCustomer.latestNote,
                        modifiedOn1: customer.modifiedOn1 || existing.baseCustomer.modifiedOn1,
                        lastNote: customer.lastNote || existing.baseCustomer.lastNote
                    };
                }
            }
        });

        // Convert map to array of aggregated customers
        return Array.from(aggregationMap.values()).map(({ baseCustomer, notesCount, keywordsSet }) => ({
            ...baseCustomer,
            notesCount,
            // Merge all distinct keywords collected across the grouped records
            keywords: Array.from(keywordsSet).join(", ")
        }));
    }, [filteredCustomers]);

    // Calculate metrics
    const metrics = useMemo(() => {
        const customers = filteredCustomers;
        const uniqueCustomerNames = new Set(customers.map(c => c.crmCustomerName));
        const uniqueSolutionNames = new Set(customers.map(c => c.crmProductName).filter(Boolean));

        // Total notes count (all notes across all engagements)
        const notesCount = customers.filter(c => c.hasNotes).length;

        return {
            // Use aggregatedCustomers length for distinct engagement count
            distinctEngagements: aggregatedCustomers.length,
            uniqueCustomers: uniqueCustomerNames.size,
            uniqueSolutions: uniqueSolutionNames.size,
            notesCount
        };
    }, [filteredCustomers, aggregatedCustomers]);

    // Chart data for CVC Leadership (L4) - use aggregated customers (one per engagement)
    const l4ManagerChartData = useMemo(() => {
        const counts = new Map<string, number>();
        aggregatedCustomers.forEach(c => {
            const manager = c.l4Manager || "Unknown";
            counts.set(manager, (counts.get(manager) || 0) + 1);
        });

        const entries: Array<{ stage: string; count: number; color: string }> = [];
        counts.forEach((count, stage) => {
            entries.push({ stage, count, color: "" });
        });
        return entries.sort((a, b) => b.count - a.count).slice(0, 10); // Top 10
    }, [aggregatedCustomers]);

    // Chart data for Solutions - use aggregated customers (one per engagement)
    const solutionChartData = useMemo(() => {
        const counts = new Map<string, number>();
        aggregatedCustomers.forEach(c => {
            const solution = c.crmProductName || "Unknown";
            counts.set(solution, (counts.get(solution) || 0) + 1);
        });

        const entries: Array<{ stage: string; count: number; color: string }> = [];
        counts.forEach((count, stage) => {
            entries.push({ stage, count, color: SOLUTION_COLORS[stage] || "" });
        });
        return entries.sort((a, b) => b.count - a.count);
    }, [aggregatedCustomers]);

    // Chart data for Last Engaged buckets - computed from baseFilteredCustomers (before lastEngaged filter)
    // This ensures the donut chart counts don't change when clicking on a bucket
    const lastEngagedBucketData = useMemo(() => {
        const bucketCounts = new Map<string, number>();
        LAST_ENGAGED_BUCKETS.forEach(b => bucketCounts.set(b.key, 0));

        // Use latestNoteDateByEngagement to count unique engagements per bucket
        // This is the same data source used for filtering, ensuring consistency
        latestNoteDateByEngagement.forEach((latestDate) => {
            const bucket = getLastEngagedBucket(latestDate);
            bucketCounts.set(bucket, (bucketCounts.get(bucket) || 0) + 1);
        });

        return LAST_ENGAGED_BUCKETS.map(b => ({
            key: b.key,
            label: b.label,
            fullLabel: b.fullLabel,
            count: bucketCounts.get(b.key) || 0,
            color: b.color
        }));
    }, [latestNoteDateByEngagement]);

    // Clear all filters
    const clearFilters = useCallback(() => {
        setSelectedCustomers([]);
        setSelectedSolutions([]);
        setSelectedL4Managers([]);
        setSelectedL5Managers([]);
        setSelectedL6Managers([]);
        setSelectedOwners([]);
        setSelectedLastEngaged([]);
        setSearchQuery("");
    }, [setSelectedCustomers, setSelectedSolutions, setSelectedL4Managers, setSelectedL5Managers, setSelectedL6Managers, setSelectedOwners, setSelectedLastEngaged]);

    // Handle notes click - fetch full notes for this customer+product on demand.
    // The main list no longer carries note text, so we load it only when the panel opens.
    const handleNotesClick = useCallback((customer: ICVCAggregatedCustomer) => {
        // Open panel and show loading state immediately
        setSelectedCustomerForNotes({
            customerName: customer.crmCustomerName,
            productName: customer.crmProductName,
            customerProductURL: customer.customerProductURL,
            notesData: []
        });
        setIsLoadingNotes(true);
        openNotesPanel();

        fetchCustomerNotes({
            customerName: customer.crmCustomerName,
            productName: customer.crmProductName
        })
            .unwrap()
            .then((notes) => {
                setSelectedCustomerForNotes({
                    customerName: customer.crmCustomerName,
                    productName: customer.crmProductName,
                    customerProductURL: customer.customerProductURL,
                    notesData: notes ?? []
                });
            })
            .catch(() => {
                setSelectedCustomerForNotes({
                    customerName: customer.crmCustomerName,
                    productName: customer.crmProductName,
                    customerProductURL: customer.customerProductURL,
                    notesData: []
                });
            })
            .finally(() => {
                setIsLoadingNotes(false);
            });
    }, [openNotesPanel, fetchCustomerNotes]);

    // Format date for display
    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    };

    // Data grid columns
    const columns: GridColDef[] = useMemo(() => [
        {
            field: "crmLink",
            headerName: "CRM Link",
            width: 85,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => {
                const url = params.row.customerProductURL;
                if (!url || !url.trim()) {
                    return null;
                }
                return (
                    <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={url}
                        style={{
                            color: "rgb(0, 120, 212)",
                            textDecoration: "none",
                            fontSize: 26,
                            cursor: "pointer"
                        }}
                    >
                        <LinkRegular />
                    </Link>
                );
            }
        },
        {
            field: "crmCustomerName",
            headerName: "Customer Name",
            flex: 1.5,
            minWidth: 180,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span style={{ fontWeight: 500, color: "#0078D4" }}>{params.value}</span>
                </TooltipHost>
            )
        },
        {
            field: "tenantId",
            headerName: "Tenant ID",
            width: 280,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span>{params.value || "-"}</span>
                </TooltipHost>
            )
        },
        {
            field: "crmProductName",
            headerName: "Solution",
            width: 220,
            renderCell: (params: GridRenderCellParams) => {
                const color = SOLUTION_COLORS[params.value] || "#0078D4";
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                            width: "4px",
                            height: "20px",
                            borderRadius: "2px",
                            background: color,
                            flexShrink: 0
                        }} />
                        <TooltipHost content={params.value}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {params.value}
                            </span>
                        </TooltipHost>
                    </div>
                );
            }
        },
        {
            field: "keywords",
            headerName: "Keywords",
            width: 220,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                const keywords = ((params.value as string) || "")
                    .split(",")
                    .map((kw) => kw.trim())
                    .filter((kw) => kw.length > 0);

                if (keywords.length === 0) {
                    return <span style={{ color: "#666" }}>-</span>;
                }

                return (
                    <TooltipHost content={keywords.join(", ")}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center" }}>
                            {keywords.map((kw, idx) => (
                                <span
                                    key={`${kw}_${idx}`}
                                    style={{
                                        display: "inline-block",
                                        padding: "2px 8px",
                                        borderRadius: "12px",
                                        background: "#EFF6FC",
                                        color: "#0F6CBD",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {kw}
                                </span>
                            ))}
                        </div>
                    </TooltipHost>
                );
            }
        },
        {
            field: "l4Manager",
            headerName: "CVC Leadership (L4)",
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span>{params.value || "-"}</span>
                </TooltipHost>
            )
        },
        {
            field: "l5Manager",
            headerName: "CVC Portfolio Lead (L5)",
            width: 170,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span>{params.value || "-"}</span>
                </TooltipHost>
            )
        },
        {
            field: "l6Manager",
            headerName: "CVC Lead (L6)",
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span>{params.value || "-"}</span>
                </TooltipHost>
            )
        },
        {
            field: "owner",
            headerName: "Customer Assigned PM",
            width: 180,
            renderCell: (params: GridRenderCellParams) => (
                <TooltipHost content={params.value}>
                    <span>{params.value || "-"}</span>
                </TooltipHost>
            )
        },
        {
            field: "modifiedOn1",
            headerName: "Notes Modified Date",
            width: 150,
            renderCell: (params: GridRenderCellParams) => formatDate(params.value),
            sortComparator: (v1: string | null, v2: string | null) => {
                const date1 = v1 ? new Date(v1).getTime() : 0;
                const date2 = v2 ? new Date(v2).getTime() : 0;
                return date1 - date2;
            }
        },
        {
            field: "notesCount",
            headerName: "Notes",
            width: 90,
            headerAlign: "center",
            align: "center",
            sortable: true,
            renderCell: (params: GridRenderCellParams) => {
                const count = params.value as number || 0;
                return (
                    <div
                        onClick={() => handleNotesClick(params.row as ICVCAggregatedCustomer)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            color: count > 0 ? "#0078D4" : "#666"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f0f0f0";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }}
                        title={`${count} note${count !== 1 ? "s" : ""} - Click to view`}
                    >
                        <Icon iconName="QuickNote" style={{ fontSize: 16 }} />
                        <span style={{ fontWeight: 600 }}>{count}</span>
                    </div>
                );
            }
        }
    ], [handleNotesClick]);

    // Convert aggregated customers to grid rows with unique IDs
    const rowsWithIndex = useMemo(() => {
        return aggregatedCustomers.map((customer, index) => ({
            ...customer,
            _uniqueRowId: `${customer.tenantId}_${customer.crmProductName}_${index}`
        }));
    }, [aggregatedCustomers]);

    const rows: IRows = {
        values: rowsWithIndex,
        count: rowsWithIndex.length
    };

    // Only show loading spinner if we don't have ANY data (fresh or cached)
    const isLoading = externalLoading || isActuallyLoading;

    if (isLoading) {
        return (
            <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.fullPageLoadingContainer}>
                <Spinner size={SpinnerSize.large} label="Loading CVC customers..." />
            </div>
        );
    }

    // Show error only if we have no data at all (including cache)
    if (error && !effectiveData) {
        return (
            <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.errorContainer}>
                <Icon iconName="ErrorBadge" style={{ fontSize: 48, color: "#e74c3c", marginBottom: 16 }} />
                <div style={{ fontSize: 18, fontWeight: 600, color: "#e74c3c", marginBottom: 8 }}>Error Loading Data</div>
                <div style={{ color: "#7f8c8d" }}>Unable to fetch CVC customer data. Please try again later.</div>
            </div>
        );
    }

    return (
        <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.tabContainer}>
            {/* Background refresh indicator - shows when fresh data is loading */}
            {isFetching && effectiveData && (
                <div style={{
                    position: "fixed",
                    top: 60,
                    right: 20,
                    zIndex: 1000,
                    backgroundColor: "#0078d4",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                }}>
                    <Spinner size={SpinnerSize.xSmall} styles={{ circle: { borderColor: "white", borderTopColor: "transparent" } }} />
                    Refreshing data...
                </div>
            )}

            {/* Filter Section - Blue gradient card */}
            <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filtersCard}>
                <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filtersTitle}>
                    <Icon iconName="Filter" style={{ marginRight: "8px" }} />
                    Filters
                </div>
                <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filtersRow}>
                    <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterGroup} style={{ width: "200px", minWidth: "180px" }}>
                        <label className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterLabel}>CUSTOMER NAME</label>
                        <SearchableMultiSelect
                            placeholder="Search Customer(s)"
                            options={filterOptions.customers}
                            selectedKeys={selectedCustomers}
                            onChange={setSelectedCustomers}
                        />
                    </div>
                    <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterGroup}>
                        <label className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterLabel}>SOLUTION</label>
                        <SearchableMultiSelect
                            placeholder="Search Solution(s)"
                            options={filterOptions.solutions}
                            selectedKeys={selectedSolutions}
                            onChange={setSelectedSolutions}
                        />
                    </div>
                    <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterGroup}>
                        <label className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterLabel}>CVC LEADERSHIP (L4)</label>
                        <SearchableMultiSelect
                            placeholder="Search Leadership(s)"
                            options={filterOptions.l4Managers}
                            selectedKeys={selectedL4Managers}
                            onChange={setSelectedL4Managers}
                        />
                    </div>
                    <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterGroup}>
                        <label className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterLabel}>CVC PORTFOLIO LEAD (L5)</label>
                        <SearchableMultiSelect
                            placeholder="Search Portfolio Lead(s)"
                            options={filterOptions.l5Managers}
                            selectedKeys={selectedL5Managers}
                            onChange={setSelectedL5Managers}
                        />
                    </div>
                    <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterGroup}>
                        <label className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterLabel}>CVC LEAD (L6)</label>
                        <SearchableMultiSelect
                            placeholder="Search CVC Lead(s)"
                            options={filterOptions.l6Managers}
                            selectedKeys={selectedL6Managers}
                            onChange={setSelectedL6Managers}
                        />
                    </div>
                    <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterGroup}>
                        <label className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterLabel}>CUSTOMER ASSIGNED PM</label>
                        <SearchableMultiSelect
                            placeholder="Search PM(s)"
                            options={filterOptions.owners}
                            selectedKeys={selectedOwners}
                            onChange={setSelectedOwners}
                        />
                    </div>
                    <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterGroup}>
                        <label className={CVC_CUSTOMER_CUSTOMERS_STYLES.filterLabel}>LAST ENGAGED</label>
                        <SearchableMultiSelect
                            placeholder="All Buckets"
                            options={LAST_ENGAGED_BUCKETS.map(b => b.key)}
                            selectedKeys={selectedLastEngaged}
                            onChange={setSelectedLastEngaged}
                            preserveOrder={true}
                        />
                    </div>
                    <button className={CVC_CUSTOMER_CUSTOMERS_STYLES.clearButton} onClick={clearFilters}>
                        Clear
                    </button>
                </div>
            </div>

            {/* AI Companion Section */}
            <CVCCustomerAICompanionSection
                totalCustomers={metrics.uniqueCustomers}
                totalEngagements={metrics.distinctEngagements}
                totalSolutions={metrics.uniqueSolutions}
            />

            {/* Stats Cards */}
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                <StatCard
                    icon="Contact"
                    iconColor="#2980b9"
                    iconBgColor="#e6f2fb"
                    value={metrics.uniqueCustomers}
                    label="Distinct Customers"
                />
                <StatCard
                    icon="ProgressRingDots"
                    iconColor="#27ae60"
                    iconBgColor="#e8f5e9"
                    value={metrics.distinctEngagements}
                    label="Customer Solution Engagements"
                />
                <StatCard
                    icon="QuickNote"
                    iconColor="#8e44ad"
                    iconBgColor="#f3e5f5"
                    value={metrics.notesCount}
                    label="Notes Count"
                />
            </div>

            {/* Donut Charts */}
            <DonutChartFilters
                l4ManagerData={l4ManagerChartData}
                selectedL4Managers={selectedL4Managers}
                onL4ManagerClick={setSelectedL4Managers}
                solutionData={solutionChartData}
                selectedSolutions={selectedSolutions}
                onSolutionClick={setSelectedSolutions}
                lastEngagedData={lastEngagedBucketData}
                selectedLastEngaged={selectedLastEngaged}
                onLastEngagedClick={setSelectedLastEngaged}
                totalEngagements={metrics.distinctEngagements}
            />

            {/* Data Grid */}
            <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.tableSection}>
                <div className={CVC_CUSTOMER_CUSTOMERS_STYLES.tableSectionHeader}>
                    <Icon iconName="Table" className={CVC_CUSTOMER_CUSTOMERS_STYLES.tableSectionIcon} />
                    <div>
                        <h3 className={CVC_CUSTOMER_CUSTOMERS_STYLES.tableSectionTitle}>Customer Engagement Details</h3>
                        <p className={CVC_CUSTOMER_CUSTOMERS_STYLES.tableSectionSubtitle}>Details captured at Customer, Solution, and Notes level</p>
                    </div>
                </div>
                <CustomDataGrid
                    columns={columns}
                    rows={rows}
                    loading={false}
                    getRowId={(row) => row._uniqueRowId}
                    paginationOptions={{ pageSize: 15, page: 0 }}
                    pageSizeOptions={[15, 25, 50, 75, 100]}
                    kustoLink={effectiveData?.cvcCustomersKustoLink}
                    TabName="CVC Customers"
                    sortingOrder={["asc", "desc"]}
                    enableUrlParams
                    initialState={{
                        sorting: {
                            sortModel: [{ field: "modifiedOn1", sort: "desc" }]
                        }
                    }}
                />
            </div>

            {/* Notes Panel */}
            {selectedCustomerForNotes && (
                <CVCCustomerNotesPanel
                    isOpen={isNotesPanelOpen}
                    onDismiss={closeNotesPanel}
                    customerName={selectedCustomerForNotes.customerName}
                    productName={selectedCustomerForNotes.productName}
                    customerProductURL={selectedCustomerForNotes.customerProductURL}
                    notesData={selectedCustomerForNotes.notesData}
                    kustoLink={effectiveData?.cvcCustomersKustoLink}
                    isLoading={isLoadingNotes}
                />
            )}
        </div>
    );
};
